import { GuidanceStepCypressAttribute } from '@/modules/guidancestep/constants/GuidanceStepCypressAttribute';
import Enum from '@/modules/core/app/helpers/Enum';
import Lang from '@/modules/core/app/services/Lang';
import { MilitaryServiceStatus } from '@/modules/contact/enums/MilitaryServiceStatus';
import { getters } from '@/modules/core/app/helpers/store';

export class ContactEmploymentDetailType extends Enum {
  key!: string;
  value!: string;
  e2EValue!: string;

  static WAS_FEDERAL_EMPLOYEE: ContactEmploymentDetailType = {
    key: 'wasFederalEmployee',
    get value(): string {
      return Lang.t('contact.federal-employee');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.WAS_FEDERAL_EMPLOYEE
  };

  static WAS_EMPLOYED: ContactEmploymentDetailType = {
    key: 'wasEmployed',
    get value(): string {
      return Lang.t('contact.employed');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.WAS_EMPLOYED
  };

  static WAS_UNEMPLOYED: ContactEmploymentDetailType = {
    key: 'wasUnemployed',
    get value(): string {
      return Lang.t('contact.unemployed');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.WAS_UNEMPLOYED
  };

  static WAS_RETIRED: ContactEmploymentDetailType = {
    key: 'wasRetired',
    get value(): string {
      return Lang.t('contact.retired');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.WAS_RETIRED
  };

  static WAS_SELF_EMPLOYED: ContactEmploymentDetailType = {
    key: 'wasSelfEmployed',
    get value(): string {
      return Lang.t('contact.self');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.WAS_SELF_EMPLOYED
  };

  static WAS_MILITARY_MEMBER: ContactEmploymentDetailType = {
    key: 'wasMilitaryMember',
    get value(): string {
      return getters.auth.getCurrentEstate()?.jurisdiction.isInCanada
        ? Lang.t('contact.canadian-forces-member')
        : Lang.t('contact.us-department-defense-member');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.WAS_MILITARY_MEMBER
  };

  static MILITARY_SERVICE_STATUS: ContactEmploymentDetailType = {
    key: 'militaryServiceStatus',
    get value(): string {
      return MilitaryServiceStatus.getByKey(
        getters.auth.getCurrentEstate()?.contact.contactEmploymentDetail?.militaryServiceStatus
      )?.value as string;
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.MILITARY_SERVICE_STATUS
  };

  static WAS_MILITARY_VETERAN: ContactEmploymentDetailType = {
    key: 'wasMilitaryVeteran',
    get value(): string {
      return Lang.t('contact.canadian-forces-veteran');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.WAS_MILITARY_VETERAN
  };

  static DIED_ON_ACTIVE_MILITARY_DUTY: ContactEmploymentDetailType = {
    key: 'diedOnActiveMilitaryDuty',
    get value(): string {
      return Lang.t('contact.an-active-duty');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.DIED_ON_ACTIVE_DUTY
  };

  static DIED_ON_INACTIVE_MILITARY_DUTY: ContactEmploymentDetailType = {
    key: 'diedOnInactiveMilitaryDuty',
    get value(): string {
      return Lang.t('contact.an-inactive-duty');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.DIED_ON_INACTIVE_DUTY
  };

  static DIED_FROM_MILITARY_SERVICE_RELATED_INJURY: ContactEmploymentDetailType = {
    key: 'diedFromMilitaryServiceRelatedInjury',
    get value(): string {
      return Lang.t('contact.recognized-military-service-injury');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.DIED_FROM_SERVICE_INJURY
  };

  static DIED_RECEIVING_VETERAN_AFFAIRS_CARE: ContactEmploymentDetailType = {
    key: 'diedReceivingVeteranAffairsCare',
    get value(): string {
      return Lang.t('contact.receiving-veteran-affairs-care');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.DIED_RECEIVING_CARE
  };

  static DIED_RECEIVING_VETERAN_AFFAIRS_COMPENSATION: ContactEmploymentDetailType = {
    key: 'diedReceivingVeteranAffairsCompensation',
    get value(): string {
      return Lang.t('contact.receiving-veteran-affairs-compensation');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.DIED_RECEIVING_COMPENSATION
  };

  static DIED_WHILE_PUBLIC_SAFETY_OFFICER_ON_DUTY: ContactEmploymentDetailType = {
    key: 'diedWhilePublicSafetyOfficerOnDuty',
    get value(): string {
      return Lang.t('contact.a-public-safety-officer-duty');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.DIED_WHILE_PUBLIC_OFFICER
  };

  static WAS_COAL_INDUSTRY_WORKER: ContactEmploymentDetailType = {
    key: 'wasCoalIndustryWorker',
    get value(): string {
      return Lang.t('contact.a-coal-industry-worker');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.WAS_COAL_INDUSTRY_WORKER
  };

  static DIED_FROM_PNEUMOCONIOSIS: ContactEmploymentDetailType = {
    key: 'diedFromPneumoconiosis',
    get value(): string {
      return Lang.t('contact.afflicted-with-pneumoconiosis');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.DIED_FROM_PNEUMOCONIOSIS
  };

  static DIED_RECEIVING_EMPLOYMENT_INSURANCE: ContactEmploymentDetailType = {
    key: 'diedReceivingEmploymentInsurance',
    get value(): string {
      return Lang.t('contact.died-employment-insurance');
    },
    e2EValue:
      GuidanceStepCypressAttribute.employmentDetails.inputs.DIED_RECEIVING_EMPLOYMENT_INSURANCE
  };

  static DIED_ELIGIBLE_FOR_BUT_NOT_RECEIVING_EMPLOYMENT_INSURANCE: ContactEmploymentDetailType = {
    key: 'diedEligibleForButNotReceivingEmploymentInsurance',
    get value(): string {
      return Lang.t('contact.eligible-not-receiving-employment');
    },
    e2EValue:
      GuidanceStepCypressAttribute.employmentDetails.inputs
        .DIED_ELIGIBLE_FOR_BUT_NOT_RECEIVING_EMPLOYMENT_INSURANCE
  };

  static DIED_RECEIVING_PROVINCIAL_INCOME_ASSISTANCE: ContactEmploymentDetailType = {
    key: 'diedReceivingProvincialIncomeAssistance',
    get value(): string {
      return Lang.t('contact.died-provincial-assistance');
    },
    e2EValue:
      GuidanceStepCypressAttribute.employmentDetails.inputs
        .DIED_RECEIVING_PROVINCIAL_INCOME_ASSISTANCE
  };

  static DIED_RECEIVING_WORKERS_COMPENSATION: ContactEmploymentDetailType = {
    key: 'diedReceivingWorkersCompensation',
    get value(): string {
      return Lang.t('contact.died-workers-compensation');
    },
    e2EValue:
      GuidanceStepCypressAttribute.employmentDetails.inputs.DIED_RECEIVING_WORKERS_COMPENSATION
  };

  static DIED_RECEIVING_FERS_RETIREMENT: ContactEmploymentDetailType = {
    key: 'diedReceivingFersRetirement',
    get value(): string {
      return Lang.t('contact.died-receiving-fers-retirement');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.DIED_RECEIVING_FERS_RETIREMENT
  };

  static DIED_ENTITLED_TO_RECEIVE_FERS_RETIREMENT: ContactEmploymentDetailType = {
    key: 'diedEntitledToReceiveFersRetirement',
    get value(): string {
      return Lang.t('contact.died-entitled-to-receive-fers-retirement');
    },
    e2EValue:
      GuidanceStepCypressAttribute.employmentDetails.inputs.DIED_ENTITLED_TO_RECEIVE_FERS_RETIREMENT
  };

  static DIED_MEMBER_OF_NATIVE_BAND: ContactEmploymentDetailType = {
    key: 'diedMemberOfNativeBand',
    get value(): string {
      return Lang.t('contact.died-member-of-native-band');
    },
    e2EValue: GuidanceStepCypressAttribute.employmentDetails.inputs.DIED_MEMBER_OF_NATIVE_BAND
  };
}
